import React, { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import jobService from "../../services/job.service";
import { JobApplicantsResponse, JobApplicationStatus } from "../../types/api";
import Loader from "../../components/ui/Loader";
import ApplicationStatusFlow from "../../components/Job/ApplicationStatusFlow";
import UserSelectionModal from "../../components/Job/UserSelectionModal";
import Button from "../../components/ui/Button";
import ConfirmationDialog from "../../components/ui/ConfirmationDialog";
import authService from "../../services/auth.service";
import { useAuth } from "../../hooks/useAuth";
import Toast from "../../components/ui/Toast";

const Applications: FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const { user } = useAuth();
  const [applicants, setApplicants] = useState<JobApplicantsResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "warning" | "info";
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedApplicant, setSelectedApplicant] =
    useState<JobApplicantsResponse | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isUserSelectionModalOpen, setIsUserSelectionModalOpen] =
    useState<boolean>(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const [reason, setReason] = useState<string>("");

  const fetchApplicants = async () => {
    if (!postId) {
      setError("Job Post ID is missing.");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await jobService.getJobApplicants(
        parseInt(postId, 10),
        1,
        100,
      );
      if (response.status == "success") {
        setApplicants(response.data.items || []);
      } else {
        setError("Failed to fetch job applicants.");
      }
    } catch (err) {
      setError("Failed to fetch job applicants.");
      console.error("Error fetching job applicants:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log("Fetching all applications");
    fetchApplicants();
  }, [postId]);

  const handleUpdateStatusClick = (applicant: JobApplicantsResponse) => {
    setSelectedApplicant(applicant);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedApplicant(null);
  };

  const handleStageSelect = async (newStatus: JobApplicationStatus) => {
    if (!selectedApplicant) return;

    try {
      setLoading(true);
      const { userId } = await authService.getMe();
      await jobService.updateJobApplication({
        applicationId: selectedApplicant.applicationId,
        newStatus: newStatus,
        modifiedBy: userId,
      });
      // Refresh the applicants list
      fetchApplicants();
      handleModalClose();
      setToast({ message: "application status updated", type: "success" });
    } catch (error) {
      console.error("Failed to update job application status:", error);
      setToast({
        message: "Failed to update application status",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // if (loading) {
  //   return (
  //     <div className="flex justify-center items-center h-screen">
  //       <Loader size="lg" />
  //     </div>
  //   );
  // }

  const handleUserSelect = (userId: string) => {
    setSelectedUserId(userId);
    // setIsUserSelectionModalOpen(false);
    setIsConfirmOpen(true);
  };

  const handleConfirmCreateApplication = async () => {
    if (!selectedUserId || !postId) return;
    try {
      const { userId } = await authService.getMe();
      await jobService.createJobApplication({
        userId: selectedUserId,
        postId: parseInt(postId, 10),
        reason,
        createdBy: userId, // Replace with actual user ID
      });
      fetchApplicants();
      setIsConfirmOpen(false);
      setSelectedUserId(null);
      setReason("");
    } catch (error) {
      console.error("Failed to create job application:", error);
    }
  };

  if (error) {
    return <div className="text-center py-12 text-red-600">Error: {error}</div>;
  }

  const applicantUserIds = applicants.map((app) => app.userId);

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          position="top-right"
          duration={2500}
          onClose={() => setToast(null)}
          show
        />
      )}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-3xl font-bold text-gray-900">Job Applications</h1>
        <div className="flex space-x-2">
          <Button onClick={fetchApplicants} disabled={loading}>
            Refresh
          </Button>
          <Button
            onClick={() => setIsUserSelectionModalOpen(true)}
            disabled={loading}
          >
            Add Application
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        {loading ? (
              <div className="flex justify-center items-center w-100%">
                <Loader size="lg" />
              </div>
            ) : (
        <table className="min-w-full bg-white">
          <thead className="bg-gray-800 text-white">
            <tr>
              <th className="py-3 px-4 uppercase font-semibold text-sm">
                User Name
              </th>
              <th className="py-3 px-4 uppercase font-semibold text-sm">
                Resume
              </th>
              <th className="py-3 px-4 uppercase font-semibold text-sm">
                Status
              </th>
              <th className="py-3 px-4 uppercase font-semibold text-sm">
                Reason
              </th>
              <th className="py-3 px-4 uppercase font-semibold text-sm">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="text-gray-700">
            {applicants.map((applicant) => (
              <tr key={applicant.applicationId}>
                <td className="py-3 px-4">{applicant.userName}</td>
                <td className="py-3 px-4">
                  {applicant.resumeUrl != null && applicant.resumeUrl != "" ? (
                    <a
                      href={applicant.resumeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline"
                    >
                      View Resume
                    </a>
                  ) : (
                    <>N/A</>
                  )}
                </td>
                <td className="py-3 px-4">{applicant.status}</td>
                <td className="py-3 px-4">{applicant.reason}</td>
                <td className="py-3 px-4">
                  <Button onClick={() => handleUpdateStatusClick(applicant)}>
                    Update Status
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>)}
      </div>
      {isModalOpen && selectedApplicant && (
        <ApplicationStatusFlow
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onStageSelect={handleStageSelect}
          currentStatus={selectedApplicant.status || "Invited"}
          userRole={user?.role}
        />
      )}
      <UserSelectionModal
        isOpen={isUserSelectionModalOpen}
        onSelect={handleUserSelect}
        onClose={() => setIsUserSelectionModalOpen(false)}
        existingApplicantIds={applicantUserIds}
      />
      <ConfirmationDialog
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmCreateApplication}
        title="Add Application"
        message="Please provide a reason for adding this application:"
        confirmText="Add"
        variant="info"
      >
        <textarea
          className="w-full p-2 border border-gray-300 rounded-md"
          rows={3}
          placeholder="Reason..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        ></textarea>
      </ConfirmationDialog>
    </div>
  );
};

export default Applications;
